import {
  findCountryCodeGivenCountryName,
  findCountryNameGivenCountryCode,
} from "./nearby-shows/utils";
import { useEffect, useMemo, useRef, useState } from "react";

import { ComboBoxResponsive } from "./ComboBoxResponsive";
import { SignInFormVariant } from "@punchup/ui-common/components/signin";
import { cn } from "./utils";
import { genAbsoluteURL } from "./utils";
import { supportedCountries } from "./phone-input/countries";

interface CityOption {
  name: string;
  state: string;
  county?: string;
  postal_code?: string;
}

interface Props {
  city: string;
  cityInputRef?: React.RefObject<HTMLInputElement>;
  onCityChange: (value: string) => void;
  defaultCountryCode: string | null;
  onCountryCodeChange: (value: string) => void;
  errors?: {
    city?: string;
    country?: string;
  };
  variant?: SignInFormVariant;
  globalSearch?: boolean;
}

// sort countries alphabetically
const countries = supportedCountries.sort((a, b) => (a.name > b.name ? 1 : -1));

export default function CitySearchFields({
  city,
  cityInputRef,
  onCityChange,
  defaultCountryCode,
  onCountryCodeChange,
  errors,
  variant = "normal",
  globalSearch = false,
}: Props) {
  const [enteredCountryValue, setEnteredCountryValue] = useState("");
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [filteredCities, setFilteredCities] = useState<CityOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const initialCountryValue = useMemo(() => {
    return findCountryNameGivenCountryCode(
      defaultCountryCode ?? ""
    ).toLowerCase();
  }, [defaultCountryCode]);

  // Debounce city search input to avoid too many API calls
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (city.trim().length < 2) {
      setDebouncedSearchTerm("");
      return;
    }

    timeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(city);
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [city]);

  // Fetch city suggestions from API
  useEffect(() => {
    const countryCode =
      findCountryCodeGivenCountryName(enteredCountryValue) ||
      defaultCountryCode;

    if (!debouncedSearchTerm || (!countryCode && !globalSearch)) {
      setFilteredCities([]);
      return;
    }

    setIsLoading(true);

    // URL parameters for the API call
    const params = new URLSearchParams({
      query: debouncedSearchTerm,
      ...(countryCode ? { countryCode } : {}),
      ...(globalSearch ? { globalSearch: "true" } : {}),
    });

    // Using a new API endpoint for city search that we'll create
    fetch(genAbsoluteURL(`/api/city-search?${params.toString()}`))
      .then((response) => response.json())
      .then((data) => {
        setFilteredCities(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching city suggestions:", error);
        setFilteredCities([]);
        setIsLoading(false);
      });
  }, [
    debouncedSearchTerm,
    enteredCountryValue,
    defaultCountryCode,
    globalSearch,
  ]);

  useEffect(() => {
    // Close dropdown when clicking outside
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowCitySuggestions(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onCountryChange = (value: string) => {
    setEnteredCountryValue(value);

    const countryCode = findCountryCodeGivenCountryName(value);

    if (countryCode) {
      onCountryCodeChange(countryCode);

      // Reset city suggestions when country changes
      setFilteredCities([]);
      if (city.trim().length >= 2) {
        // Trigger new search with updated country
        setDebouncedSearchTerm(city);
      }
    }
  };

  const handleCityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onCityChange(e.target.value);
    setShowCitySuggestions(true);
  };

  const handleCitySelect = (cityOption: CityOption) => {
    // We want to update the city field with a well-formatted city name
    const fullCityName = cityOption.county
      ? `${cityOption.name}, ${cityOption.county}, ${cityOption.state}`
      : `${cityOption.name}, ${cityOption.state}`;

    onCityChange(fullCityName);
    setShowCitySuggestions(false);
  };

  return (
    <div className="flex flex-col mb-2">
      <div
        className={cn(
          "flex flex-row",
          variant === "minimal" ? "gap-x-3 md:gap-x-4" : "gap-x-4 md:gap-x-5"
        )}
      >
        <div className="flex-1 relative" ref={dropdownRef}>
          <label
            htmlFor="city-input"
            className={cn(
              variant === "minimal"
                ? "sr-only"
                : "font-sans font-semibold text-[16px] leading-7 mb-1"
            )}
          >
            City
          </label>
          <input
            ref={cityInputRef}
            title="city-input"
            id="city-input"
            name="city"
            type="text"
            placeholder="Enter city name (e.g. Chicago, IL)"
            autoComplete="off"
            onFocus={() => setShowCitySuggestions(true)}
            onChange={handleCityInputChange}
            value={city}
            className={cn(
              "w-full font-sans text-[16px] leading-6 placeholder:text-tertiary-blue border border-secondary-blue rounded-[10px]",
              variant === "minimal" ? "px-3 py-2.5" : "px-4 py-3"
            )}
          />
          {showCitySuggestions && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
              {isLoading ? (
                <div className="px-4 py-2 text-sm text-gray-500">
                  Loading suggestions...
                </div>
              ) : filteredCities.length > 0 ? (
                <ul className="py-1">
                  {filteredCities.map((cityOption, index) => (
                    <li
                      key={index}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      onClick={() => handleCitySelect(cityOption)}
                    >
                      {cityOption.county
                        ? `${cityOption.name}, ${cityOption.county}, ${cityOption.state}`
                        : `${cityOption.name}, ${cityOption.state}`}
                    </li>
                  ))}
                </ul>
              ) : debouncedSearchTerm ? (
                <div className="px-4 py-2 text-sm text-gray-500">
                  No matches found
                </div>
              ) : city.trim().length < 2 ? (
                <div className="px-4 py-2 text-sm text-gray-500">
                  Type at least 2 characters
                </div>
              ) : null}
            </div>
          )}
        </div>
        <div className="w-[120px]">
          <label
            htmlFor="country"
            className={cn(
              variant === "minimal"
                ? "sr-only"
                : "font-sans font-semibold text-[16px] leading-7 mb-1"
            )}
          >
            Country
          </label>
          <ComboBoxResponsive
            value={enteredCountryValue || initialCountryValue}
            setValue={onCountryChange}
            variant={variant}
            label="country"
            options={countries.map((country) => ({
              label: `${country.flag} ${country.name}`,
              shortLabel: `${country.flag} ${country.code}`,
              value: country.name.toLowerCase(), // the country value for the input should be the country name so that it's easily searchable by the user
            }))}
          />
          {errors?.country && (
            <span className="text-xs text-red-500 pt-1">{errors.country}</span>
          )}
        </div>
      </div>
      {errors?.city && (
        <span className="text-xs text-red-500 pt-1">{errors.city}</span>
      )}
    </div>
  );
}
